import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import makeStyles from "@mui/styles/makeStyles";
import { FormDefinition } from "developer/formsCRUD/store/formDefinitionsSlice";
import { Workspace } from "developer/workspaces/model";
import { FormEventHandler, useEffect } from "react";
import { Field, useFormState } from "react-final-form";

export type FormState = {
  getFormIsDirty: () => boolean;
};

type FormDefinitionTabProps = {
  formDefinition: FormDefinition;
  handleSubmit: FormEventHandler<HTMLFormElement>;
  workspaces: Workspace[];
  onMount: (formState: FormState) => void;
};

const useStyles = makeStyles({
  root: {
    height: "100%",
  },
  content: {
    overflow: "auto",
  },
  input: {
    width: "100%",
  },
  textarea: {
    width: "100%",
  },
  error: {
    color: "red",
  },
});

const FormDefinitionTab = (props: FormDefinitionTabProps) => {
  const { formDefinition, handleSubmit, workspaces, onMount } = props;
  const formState = useFormState();

  useEffect(() => {
    onMount({ getFormIsDirty: () => formState.dirty });
  }, [onMount, formState.dirty]);

  const classes = useStyles();

  const required = (value: string) => (value ? undefined : "Name is required");

  return (
    <form onSubmit={handleSubmit} className={classes.root}>
      <Grid container direction="column" className={classes.root} spacing={1}>
        <Grid
          container
          item
          xs
          direction="column"
          spacing={2}
          wrap="nowrap"
          className={classes.content}
        >
          <Grid item>
            <Field
              id="name"
              name="name"
              initialValue={formDefinition.name}
              validate={required}
            >
              {({ input, meta }) => (
                <div>
                  <TextField
                    name={input.name}
                    value={input.value}
                    size="small"
                    onChange={input.onChange}
                    className={classes.input}
                    label="Name"
                    variant="filled"
                  />
                  {meta.error && meta.touched && (
                    <span className={classes.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field
              id="description"
              name="description"
              initialValue={formDefinition.description}
            >
              {({ input }) => (
                <div>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={Infinity}
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    className={classes.textarea}
                    label="Description"
                    variant="filled"
                    size="small"
                  />
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field
              id="workspaceId"
              name="workspaceId"
              initialValue={formDefinition.workspaceId}
            >
              {({ input }) => (
                <div>
                  <FormControl variant="filled" fullWidth size="small">
                    <InputLabel id="select-workspace">Workspace</InputLabel>
                    <Select
                      fullWidth
                      labelId="select-workspace"
                      id="select-workspace"
                      value={input.value}
                      onChange={input.onChange}
                      label="Workspace"
                      name={input.name}
                    >
                      {workspaces.map((item) => (
                        <MenuItem value={item.id!}>{item.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Connect</Typography>
          </Grid>
          <Grid item>
            <Field
              id="displayName"
              name="displayName"
              initialValue={formDefinition.displayName ?? ""}
            >
              {({ input, meta }) => (
                <div>
                  <TextField
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    className={classes.input}
                    label="Display Name"
                    variant="filled"
                    size="small"
                  />
                  {meta.error && meta.touched && (
                    <span className={classes.error}>{meta.error}</span>
                  )}
                </div>
              )}
            </Field>
          </Grid>

          <Grid item>
            <Field
              id="displayDescription"
              name="displayDescription"
              initialValue={formDefinition.displayDescription ?? ""}
            >
              {({ input }) => (
                <div>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={Infinity}
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    className={classes.textarea}
                    label="Display Description"
                    variant="filled"
                    size="small"
                  />
                </div>
              )}
            </Field>
          </Grid>
          <Grid item>
            <Field
              id="connect"
              name="connect"
              initialValue={formDefinition.connect ?? false}
            >
              {({ input }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={input.value}
                      color="primary"
                    />
                  }
                  label="Connect Only"
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                />
              )}
            </Field>
            <Field
              id="disabled"
              name="disabled"
              initialValue={formDefinition.disabled}
            >
              {({ input }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={input.value}
                      onChange={input.onChange}
                    />
                  }
                  label="Disabled"
                />
              )}
            </Field>
          </Grid>
        </Grid>
        <Grid container item justifyContent="flex-end">
          <Grid item>
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default FormDefinitionTab;
